import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Users, Package, CreditCard, BarChart3, Search, Trash2, Ban, Coins, Eye, CheckCircle, XCircle, Clock, AlertCircle, Settings, Percent } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/admin/users').then(r => setUsers(r.data.users)).catch(() => {});
    api.get('/admin/products').then(r => setProducts(r.data.products)).catch(() => {});
    api.get('/admin/transactions').then(r => setTransactions(r.data.transactions)).catch(() => {});
    api.get('/admin/credit-requests?status=pending').then(r => setCreditRequests(r.data.requests)).catch(() => {});
    api.get('/escrow/admin/all').then(r => setEscrowTransactions(r.data.transactions)).catch(() => {});
    api.get('/escrow/admin/disputes').then(r => setDisputes(r.data.transactions)).catch(() => {});
    api.get('/admin/settings/platform-fee').then(r => { setPlatformFee(r.data.platform_fee_percent); setNewFee(String(r.data.platform_fee_percent)); }).catch(() => {});
  }, [user, navigate]);

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Remover este produto?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch {}
  };

  const handleSuspendUser = async (id, suspend) => {
    try {
      await api.put(`/admin/users/${id}/suspend`, { suspended: suspend });
      setUsers(prev => prev.map(u => u._id === id ? { ...u, suspended: suspend } : u));
    } catch {}
  };

  const handleUpdateCredits = async (id, credits) => {
    const newCredits = prompt('Novo saldo (R$):', credits);
    if (newCredits === null) return;
    try {
      await api.put(`/admin/users/${id}/credits`, { credits: parseInt(newCredits) });
      setUsers(prev => prev.map(u => u._id === id ? { ...u, credits: parseInt(newCredits) } : u));
    } catch {}
  };

  const handleApproveCredit = async (reqId) => {
    try {
      await api.put(`/admin/credit-requests/${reqId}/approve`);
      setCreditRequests(prev => prev.filter(r => r._id !== reqId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const handleRejectCredit = async (reqId) => {
    if (!window.confirm('Rejeitar esta solicitação?')) return;
    try {
      await api.put(`/admin/credit-requests/${reqId}/reject`);
      setCreditRequests(prev => prev.filter(r => r._id !== reqId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao rejeitar');
    }
  };

  const handleUpdateTransactionStatus = async (txId, newStatus) => {
    const labels = { shipped: 'Marcar como Enviado', delivered: 'Marcar como Entregue', completed: 'Concluir Transação', cancelled: 'Cancelar Transação' };
    if (newStatus === 'cancelled' && !window.confirm('Cancelar esta transação?')) return;
    try {
      await api.put(`/transactions/${txId}/status`, { status: newStatus });
      setTransactions(prev => prev.map(tx => tx._id === txId ? { ...tx, status: newStatus } : tx));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const [escrowTransactions, setEscrowTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [platformFee, setPlatformFee] = useState(6);
  const [newFee, setNewFee] = useState('');

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusMap = { pending: 'Pendente', shipped: 'Enviado', delivered: 'Entregue', completed: 'Concluído', cancelled: 'Cancelado' };
  const escrowStatusMap = {
    awaiting_payment: 'Aguardando Pagamento', paid: 'Pago (Custódia)', shipped: 'Enviado',
    completed: 'Concluído', disputed: 'Em Disputa', refunded: 'Reembolsado',
    cancelled: 'Cancelado', expired: 'Expirado'
  };
  const escrowStatusColor = {
    awaiting_payment: 'bg-amber-50 text-amber-700', paid: 'bg-blue-50 text-blue-700',
    shipped: 'bg-indigo-50 text-indigo-700', completed: 'bg-emerald-50 text-emerald-700',
    disputed: 'bg-red-50 text-red-700', refunded: 'bg-orange-50 text-orange-700',
    cancelled: 'bg-zinc-100 text-zinc-600', expired: 'bg-zinc-100 text-zinc-500'
  };

  const handleEscrowAdminAction = async (txId, action) => {
    try {
      const { data } = await api.put(`/escrow/transaction/${txId}/action`, { action });
      alert(data.message);
      api.get('/escrow/admin/all').then(r => setEscrowTransactions(r.data.transactions)).catch(() => {});
      api.get('/escrow/admin/disputes').then(r => setDisputes(r.data.transactions)).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro');
    }
  };

  const handleConfirmPayment = async (txId) => {
    if (!window.confirm('Confirmar que o pagamento foi recebido?')) return;
    try {
      const { data } = await api.post(`/escrow/admin/confirm-payment/${txId}`);
      alert(data.message);
      api.get('/escrow/admin/all').then(r => setEscrowTransactions(r.data.transactions)).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  const handleUpdateFee = async () => {
    const val = parseFloat(newFee);
    if (isNaN(val) || val < 0 || val > 50) { toast.error('Taxa deve ser entre 0% e 50%'); return; }
    try {
      const { data } = await api.put('/admin/settings/platform-fee', { platform_fee_percent: val });
      setPlatformFee(data.platform_fee_percent);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar taxa');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>Painel Administrativo</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Estatísticas</TabsTrigger>
          <TabsTrigger value="credit-requests" data-testid="admin-tab-credit-requests" className="relative">
            Créditos
            {creditRequests.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground">{creditRequests.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Usuários</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Produtos</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="admin-tab-transactions">Transações</TabsTrigger>
          <TabsTrigger value="escrow" data-testid="admin-tab-escrow" className="relative">
            Escrow
            {disputes.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-500 text-white">{disputes.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">
            <Settings className="h-3.5 w-3.5 mr-1" /> Config
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stats">
          {stats && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
              {[
                { label: 'Usuários', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
                { label: 'Produtos Ativos', value: stats.active_products, icon: Package, color: 'text-green-600 bg-green-50' },
                { label: 'Transações', value: stats.total_transactions, icon: CreditCard, color: 'text-purple-600 bg-purple-50' },
                { label: 'Receita Total', value: `R$ ${(stats.total_revenue || 0).toFixed(2)}`, icon: BarChart3, color: 'text-amber-600 bg-amber-50' },
              ].map(s => (
                <div key={s.label} className="p-6 rounded-xl border border-border bg-card">
                  <div className={`inline-flex p-2 rounded-lg ${s.color} mb-3`}><s.icon className="h-5 w-5" /></div>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="credit-requests">
          {creditRequests.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <h3 className="font-semibold mb-1">Nenhuma solicitação pendente</h3>
              <p className="text-sm text-muted-foreground">Todas as solicitações foram processadas.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="credit-requests-list">
              {creditRequests.map(req => (
                <div key={req._id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-amber-200 bg-amber-50/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm">{req.user_name}</p>
                      <Badge variant="outline" className="text-[10px]">{req.user_email}</Badge>
                    </div>
                    <p className="text-sm">
                      <strong>{req.package_name}</strong> - R$ {Number(req.credits).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Via {req.payment_method === 'pix' ? 'PIX' : 'Cripto'} - {new Date(req.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleApproveCredit(req._id)}
                      className="gap-1.5 bg-green-600 hover:bg-green-700 rounded-lg"
                      data-testid={`approve-credit-${req._id}`}
                    >
                      <CheckCircle className="h-4 w-4" /> Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectCredit(req._id)}
                      className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 rounded-lg"
                      data-testid={`reject-credit-${req._id}`}
                    >
                      <XCircle className="h-4 w-4" /> Rejeitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="users">
          <div className="mb-4">
            <Input placeholder="Buscar usuários..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="max-w-sm" data-testid="admin-search-users" />
          </div>
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Nome</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-left p-3 font-medium">Créditos</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u._id} className="border-t border-border">
                      <td className="p-3">{u.name}</td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3">
                        <button onClick={() => handleUpdateCredits(u._id, u.credits)} className="flex items-center gap-1 hover:text-primary" data-testid={`admin-credits-${u._id}`}>
                          <Coins className="h-3 w-3" /> R$ {(u.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </button>
                      </td>
                      <td className="p-3">
                        {u.suspended ? <Badge variant="destructive">Suspenso</Badge> : <Badge variant="secondary">{u.role}</Badge>}
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => handleSuspendUser(u._id, !u.suspended)} data-testid={`admin-suspend-${u._id}`}>
                          <Ban className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="space-y-3" data-testid="admin-products-list">
            {products.map(p => (
              <div key={p._id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.category} - {p.seller_name}</p>
                </div>
                <Badge variant="secondary">{p.status}</Badge>
                <p className="font-bold text-primary text-sm">R$ {Number(p.price).toFixed(2)}</p>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(p._id)} data-testid={`admin-delete-${p._id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <div className="space-y-3" data-testid="admin-transactions-list">
            {transactions.map(tx => (
              <div key={tx._id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-border bg-card">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{tx.product_title}</p>
                  <p className="text-xs text-muted-foreground">{tx.buyer_name} → {tx.seller_name}</p>
                </div>
                <Badge variant="secondary">{statusMap[tx.status] || tx.status}</Badge>
                <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.product_price).toFixed(2)}</p>
                <div className="flex items-center gap-2 shrink-0">
                  {tx.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => handleUpdateTransactionStatus(tx._id, 'shipped')} className="gap-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs" data-testid={`tx-ship-${tx._id}`}>
                        <CheckCircle className="h-3.5 w-3.5" /> Enviado
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateTransactionStatus(tx._id, 'cancelled')} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 rounded-lg text-xs" data-testid={`tx-cancel-${tx._id}`}>
                        <XCircle className="h-3.5 w-3.5" /> Cancelar
                      </Button>
                    </>
                  )}
                  {tx.status === 'shipped' && (
                    <Button size="sm" onClick={() => handleUpdateTransactionStatus(tx._id, 'delivered')} className="gap-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-xs" data-testid={`tx-deliver-${tx._id}`}>
                      <CheckCircle className="h-3.5 w-3.5" /> Entregue
                    </Button>
                  )}
                  {tx.status === 'delivered' && (
                    <Button size="sm" onClick={() => handleUpdateTransactionStatus(tx._id, 'completed')} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-xs" data-testid={`tx-complete-${tx._id}`}>
                      <CheckCircle className="h-3.5 w-3.5" /> Concluir
                    </Button>
                  )}
                  {tx.status === 'completed' && (
                    <Badge className="bg-emerald-50 text-emerald-700 text-xs">Finalizada</Badge>
                  )}
                  {tx.status === 'cancelled' && (
                    <Badge className="bg-red-50 text-red-700 text-xs">Cancelada</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="escrow">
          <div className="space-y-6">
            {/* Disputes Section */}
            {disputes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" /> Disputas Abertas ({disputes.length})
                </h3>
                <div className="space-y-3">
                  {disputes.map(tx => (
                    <div key={tx._id} className="p-4 rounded-xl border-2 border-red-200 bg-red-50/50" data-testid={`dispute-${tx._id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{tx.product_title}</p>
                          <p className="text-xs text-muted-foreground">{tx.buyer_name} → {tx.seller_name}</p>
                          <p className="text-xs text-red-600 mt-1">Motivo: {tx.dispute_reason}</p>
                          <p className="text-xs text-muted-foreground">Valor: R$ {Number(tx.product_price).toFixed(2)} | Cobrado: R$ {Number(tx.total_charged).toFixed(2)}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => handleEscrowAdminAction(tx._id, 'resolve_seller')} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-xs rounded-lg">
                            <CheckCircle className="h-3.5 w-3.5" /> Favor Vendedor
                          </Button>
                          <Button size="sm" onClick={() => handleEscrowAdminAction(tx._id, 'resolve_buyer')} className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-xs rounded-lg">
                            <XCircle className="h-3.5 w-3.5" /> Reembolsar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Escrow Transactions */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Transações Escrow ({escrowTransactions.length})</h3>
              <div className="space-y-3" data-testid="escrow-transactions-list">
                {escrowTransactions.map(tx => (
                  <div key={tx._id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-border bg-card">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{tx.product_title}</p>
                      <p className="text-xs text-muted-foreground">{tx.buyer_name} → {tx.seller_name}</p>
                      <p className="text-xs text-muted-foreground">Método: {tx.payment_method === 'pix' ? 'PIX' : 'Cripto'}</p>
                    </div>
                    <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.total_charged || 0).toFixed(2)}</p>
                    <Badge className={escrowStatusColor[tx.status]}>{escrowStatusMap[tx.status]}</Badge>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      {tx.status === 'awaiting_payment' && (
                        <Button size="sm" onClick={() => handleConfirmPayment(tx._id)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-xs rounded-lg" data-testid={`confirm-payment-${tx._id}`}>
                          <CheckCircle className="h-3.5 w-3.5" /> Confirmar Pagamento
                        </Button>
                      )}
                      {!['completed', 'refunded', 'cancelled', 'expired'].includes(tx.status) && (
                        <Button size="sm" variant="outline" onClick={() => handleEscrowAdminAction(tx._id, 'cancel')} className="gap-1.5 text-destructive border-destructive/30 text-xs rounded-lg">
                          <XCircle className="h-3.5 w-3.5" /> Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {escrowTransactions.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">Nenhuma transação escrow ainda.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="max-w-lg space-y-6">
            <div className="p-6 rounded-xl border border-border bg-card" data-testid="platform-fee-settings">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex p-2 rounded-lg bg-amber-50 text-amber-600"><Percent className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-semibold text-base" style={{ fontFamily: 'Outfit' }}>Taxa de Negociação</h3>
                  <p className="text-xs text-muted-foreground">Cobrada do vendedor ao concluir uma venda</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <p className="text-sm text-muted-foreground">Taxa atual:</p>
                <Badge className="bg-emerald-50 text-emerald-700 text-base px-3 py-1" data-testid="current-fee-badge">{platformFee}%</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="0.1" min="0" max="50"
                  placeholder="Nova taxa (%)"
                  value={newFee}
                  onChange={e => setNewFee(e.target.value)}
                  className="max-w-[120px]"
                  data-testid="new-fee-input"
                />
                <span className="text-sm text-muted-foreground">%</span>
                <Button onClick={handleUpdateFee} className="gap-1.5" data-testid="update-fee-btn">
                  Salvar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Exemplo: para um produto de R$ 100,00 com taxa de {newFee || platformFee}%, o vendedor recebe R$ {(100 - 100 * (parseFloat(newFee || platformFee) || 0) / 100).toFixed(2)}</p>
            </div>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
